import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {SkippableBenchlingQueryResult, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';

import {
  ProcessFlowchartConfigEditorQuery,
  ProcessFlowchartConfigEditorQueryVariables,
} from './__generated/ProcessFlowchartConfigEditorQuery';
import {
  ProcessFlowchartConfigEditorWithDefinitionsQuery,
  ProcessFlowchartConfigEditorWithDefinitionsQueryVariables,
} from './__generated/ProcessFlowchartConfigEditorWithDefinitionsQuery';
import {
  ProcessFlowchartConfigViewerQuery,
  ProcessFlowchartConfigViewerQueryVariables,
} from './__generated/ProcessFlowchartConfigViewerQuery';

export type {ProcessFlowchartConfigVersionWithDefinitionsQueryGQL as ProcessFlowchartConfigVersionWithDefinitionsGQL} from './processFlowchartConfigEditorConstants';

export const PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY: DocumentNode = gql`
  query ProcessFlowchartConfigEditorQuery(
    $flowchartConfigId: StrictString!
    $flowchartConfigVersionId: StrictString
  ) @skipAudit @engComponent(name: WORKFLOWS) {
    processFlowchartConfig(id: $flowchartConfigId) {
      ... on ProcessFlowchartConfigNode {
        ...ProcessFlowchartConfigEditorGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartConfigEditor').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PROCESS_FLOWCHART_CONFIG_EDITOR_WITH_DEFINITIONS_QUERY: DocumentNode = gql`
  query ProcessFlowchartConfigEditorWithDefinitionsQuery(
    $flowchartConfigId: StrictString!
    $flowchartConfigVersionId: StrictString
  ) @skipAudit @engComponent(name: WORKFLOWS) {
    processFlowchartConfig(id: $flowchartConfigId) {
      ... on ProcessFlowchartConfigNode {
        ...ProcessFlowchartConfigEditorWithDefinitionsGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartConfigEditorWithDefinitions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Hook to load a process flowchart config from the server.
 *
 * @param flowchartConfigId - The ID of the flowchart config to load, or null to skip.
 * @param flowchartConfigVersionId - Optional version ID override. If provided, loads this
 *   specific version instead of the default (current) version.
 * @param options - Optional configuration. Set `networkOnly: true` to bypass the Apollo cache.
 */
export function useProcessFlowchartConfigQuery(
  flowchartConfigId: string | null,
  flowchartConfigVersionId?: string | null,
  options?: {networkOnly?: boolean}
): SkippableBenchlingQueryResult<
  ProcessFlowchartConfigEditorQuery,
  ProcessFlowchartConfigEditorQueryVariables
> {
  return useSkippableBenchlingQuery<
    ProcessFlowchartConfigEditorQuery,
    ProcessFlowchartConfigEditorQueryVariables
  >(PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY, {
    variables: {
      flowchartConfigId: flowchartConfigId ?? '',
      flowchartConfigVersionId: flowchartConfigVersionId ?? undefined,
    },
    skip: flowchartConfigId == null,
    ...(options?.networkOnly && {fetchPolicy: 'network-only' as const}),
  });
}

/**
 * Hook to load a process flowchart config including the full available task definitions.
 * Used by the ACM/PCM editors, which need field definitions and application data to build
 * data flow edges and the add-node sidepanel.
 *
 * @param flowchartConfigId - The ID of the flowchart config to load, or null to skip.
 * @param flowchartConfigVersionId - Optional version ID override.
 */
export function useProcessFlowchartConfigWithDefinitionsQuery(
  flowchartConfigId: string | null,
  flowchartConfigVersionId?: string | null,
  options?: {networkOnly?: boolean; skip?: boolean}
): SkippableBenchlingQueryResult<
  ProcessFlowchartConfigEditorWithDefinitionsQuery,
  ProcessFlowchartConfigEditorWithDefinitionsQueryVariables
> {
  return useSkippableBenchlingQuery<
    ProcessFlowchartConfigEditorWithDefinitionsQuery,
    ProcessFlowchartConfigEditorWithDefinitionsQueryVariables
  >(PROCESS_FLOWCHART_CONFIG_EDITOR_WITH_DEFINITIONS_QUERY, {
    variables: {
      flowchartConfigId: flowchartConfigId ?? '',
      flowchartConfigVersionId: flowchartConfigVersionId ?? undefined,
    },
    skip: flowchartConfigId == null || (options?.skip ?? false),
    ...(options?.networkOnly && {fetchPolicy: 'network-only' as const}),
  });
}

export const PROCESS_FLOWCHART_CONFIG_VIEWER_QUERY: DocumentNode = gql`
  query ProcessFlowchartConfigViewerQuery(
    $flowchartConfigId: StrictString!
    $flowchartConfigVersionId: StrictString
  ) @skipAudit @engComponent(name: WORKFLOWS) {
    processFlowchartConfig(id: $flowchartConfigId) {
      ... on ProcessFlowchartConfigNode {
        ...ProcessFlowchartConfigViewerGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartConfigViewer').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Hook to load a process flowchart config with slim task definitions for read-only viewing.
 * Used by the execution viewer (EXECUTION_VIEWER), which renders the flowchart structure but
 * does not build data flow edges, so it can skip the expensive field-definition/application-data
 * payload that the ACM/PCM editors require.
 *
 * @param flowchartConfigId - The ID of the flowchart config to load, or null to skip.
 * @param flowchartConfigVersionId - Optional version ID override.
 */
export function useProcessFlowchartConfigViewerQuery(
  flowchartConfigId: string | null,
  flowchartConfigVersionId?: string | null,
  options?: {networkOnly?: boolean; skip?: boolean}
): SkippableBenchlingQueryResult<
  ProcessFlowchartConfigViewerQuery,
  ProcessFlowchartConfigViewerQueryVariables
> {
  return useSkippableBenchlingQuery<
    ProcessFlowchartConfigViewerQuery,
    ProcessFlowchartConfigViewerQueryVariables
  >(PROCESS_FLOWCHART_CONFIG_VIEWER_QUERY, {
    variables: {
      flowchartConfigId: flowchartConfigId ?? '',
      flowchartConfigVersionId: flowchartConfigVersionId ?? undefined,
    },
    skip: flowchartConfigId == null || (options?.skip ?? false),
    ...(options?.networkOnly && {fetchPolicy: 'network-only' as const}),
  });
}
